import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Checkbox, DialogActions, Grid, Typography } from '@material-ui/core';
import { DialogProps } from '@material-ui/core/Dialog';
import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import classNames from 'classnames';
import TextField from 'core/TextField';
import { Form, Formik } from 'formik';
import { get } from 'lodash';
import * as Yup from 'yup';

import { selectCurrentUser, selectLocale } from '@lib/selectors';

import { snackbarSuccess } from 'actions/snackbarMessage';
import DialogHeader from 'components/DialogHeader';
import SelectField from 'components/SelectField';
import UserContactFieldSelect from 'components/UserContactFieldSelect';
import { makeSelectLanguages } from 'containers/App/selectors';
import {
  CHINESE_CULTURE_LANGUAGE_OPTIONS,
  CHINESE_LANGUAGE_ID,
  TRADITIONAL_CHINESE_LANGUAGE_ID,
} from 'containers/PatientPage/constants';
import useReduxMutation from 'hooks/useReduxMutation';
import { actionPatientCreate } from 'sagas/sagaWatchersPatient';
import { patientRequestInterceptor } from 'utils/interceptorsPatient';

import Dialog from './Dialog';
import DialogContent from './DialogContent';

const styles = ({
  palette: {
    common: { black, white },
    text,
  },
  custom: {
    palette: { colorSupportPowder },
  },
  breakpoints: { down },
  spacing: { unit },
}: Theme) =>
  createStyles({
    content: {
      [down('xs')]: {
        paddingLeft: unit,
        paddingRight: unit,
      },
    },
    footer: {
      marginTop: unit,
      textAlign: 'right',
    },
    container: {
      backgroundColor: white,
      margin: 0,
      width: '100%',
    },
    checkboxWrapper: {
      display: 'flex',
      alignItems: 'center',
    },
    checkbox: {
      padding: 0,
      marginRight: 12,
      marginTop: unit * 2,
    },
    checkboxLabel: {
      color: text.secondary,
      fontSize: 12,
      fontWeight: 500,
      marginTop: unit * 2,
    },
    button: {
      marginLeft: unit,
    },
    buttonLabel: {
      padding: `0px ${unit * 2}px`,
    },
    fieldLabel: {
      color: black,
      fontSize: 20,
      fontWeight: 600,
    },
    contactFieldLabel: {
      marginLeft: unit / 2,
      marginBottom: unit,
      marginTop: unit,
    },
    input: {
      borderColor: colorSupportPowder,
    },
  });

const initialValues = {
  firstname: '',
  lastname: '',
  dateOfBirth: '',
  contact: {
    active: true,
    type: 'sms',
    value: '',
  },
  language: 'en',
  ehrEnable: false,
  ehrId: '',
  culture: 'cn',
};
const FORM_NAME = 'formHubAddPatient';
const phoneRegExp = /^(\+[0-9]{1,2} )?\(?([2-9]{1})([0-9]{2})\)?[-. ]?([2-9]{1})([0-9]{2})[-. ]?([0-9]{4})$/;
const dateRegExp = /^(0[1-9]|1[012])[- /.](0[1-9]|[12][0-9]|3[01])[- /.](19|20)\d\d$/;
const validationSchema = Yup.object().shape({
  firstname: Yup.string().required(),
  lastname: Yup.string().required(),
  dateOfBirth: Yup.string()
    .required()
    .matches(dateRegExp, 'Invalid date'),
  contact: Yup.object({
    value: Yup.string()
      .when('type', {
        is: 'email',
        then: Yup.string().email('Invalid email address'),
        otherwise: Yup.string().matches(phoneRegExp, 'Not a valid US phone number'),
      })
      .required('Required'),
  }),
});

export interface DialogPatientCreateProps extends Omit<DialogProps, 'classes' | 'onClose'>, WithStyles<typeof styles> {
  onClose: (newPatientCreated?: boolean) => void;
}

const DialogPatientCreate: React.FC<DialogPatientCreateProps> = ({ classes, onClose, ...rest }) => {
  const dispatch = useDispatch();
  const languages = useSelector(makeSelectLanguages());
  const currentLocale = useSelector(selectLocale) || 'en-US';
  const currentUser = useSelector(selectCurrentUser);
  const linkToEhrOption = get(currentUser, 'settings.patients.linkToEhrOption.enabled', false);
  const isInitialValid = validationSchema.isValidSync(initialValues);
  const [execute] = useReduxMutation(actionPatientCreate);

  // prevent to display whatsapp as a contact type for other locales
  const excludedOptions = currentLocale !== 'pt-BR' ? ['whatsapp'] : [];

  const submit = useCallback(
    (values) => {
      execute({
        method: 'post',
        url: '/patients',
        data: {
          ...patientRequestInterceptor({ ...values, contact: [values.contact] }),
          // source is used on the backend to apply the rules of auto-assignment when a patient is created via hub
          source: 'hub',
        },
        callbackSuccess: () => {
          dispatch(snackbarSuccess('Patient saved successfully!'));
          onClose(true);
        },
      });
    },
    [execute, dispatch, onClose],
  );

  return (
    <Dialog onClose={() => onClose()} {...rest}>
      <DialogHeader onRequestClose={() => onClose()} title={'Create new patient'} />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        isInitialValid={isInitialValid}
        onSubmit={submit}
      >
        {({ values, setFieldTouched, setFieldValue, isSubmitting, touched, errors }) => {
          const selectedLanguage = values.language;
          const isChineseLanguage =
            selectedLanguage === CHINESE_LANGUAGE_ID || selectedLanguage === TRADITIONAL_CHINESE_LANGUAGE_ID;

          return (
            <React.Fragment>
              <DialogContent className={classes.content}>
                <Form id={FORM_NAME}>
                  <Grid container spacing={16} className={classes.container}>
                    <Grid item xs={6}>
                      <TextField
                        placeholder="First Name"
                        value={values.firstname}
                        onChange={(e) => setFieldValue('firstname', e.target.value)}
                        onBlur={() => setFieldTouched('firstname', true)}
                        error={Boolean(touched.firstname && errors.firstname)}
                        helperText={touched.firstname && errors.firstname && 'First Name is required.'}
                        InputProps={{
                          classes: {
                            input: classes.input,
                          },
                          'data-lh-id': 'create-patient-dialog-first-name',
                        }}
                        label="First Name"
                        InputLabelProps={{
                          className: classes.fieldLabel,
                          shrink: true,
                        }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        placeholder="Last Name"
                        value={values.lastname}
                        onChange={(e) => setFieldValue('lastname', e.target.value)}
                        onBlur={() => setFieldTouched('lastname', true)}
                        error={Boolean(touched.lastname && errors.lastname)}
                        helperText={touched.lastname && errors.lastname && 'Last Name is required.'}
                        InputProps={{
                          classes: {
                            input: classes.input,
                          },
                          'data-lh-id': 'create-patient-dialog-last-name',
                        }}
                        label="Last Name"
                        InputLabelProps={{
                          className: classes.fieldLabel,
                          shrink: true,
                        }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item sm={6}>
                      <TextField
                        value={values.dateOfBirth}
                        onChange={(e) => setFieldValue('dateOfBirth', e.target.value)}
                        onBlur={() => setFieldTouched('dateOfBirth', true)}
                        error={Boolean(touched.dateOfBirth && errors.dateOfBirth)}
                        helperText={touched.dateOfBirth && errors.dateOfBirth && 'Invalid date'}
                        placeholder="mm/dd/yyyy"
                        mask="date"
                        InputProps={{
                          'data-lh-id': 'create-patient-dialog-date-of-birth',
                          classes: {
                            input: classes.input,
                          },
                        }}
                        label="Date of Birth"
                        InputLabelProps={{
                          className: classes.fieldLabel,
                          shrink: true,
                        }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item sm={6}>
                      <SelectField
                        value={values.language}
                        // @ts-ignore
                        onChange={(value: string) => setFieldValue('language', value)}
                        onBlur={() => setFieldTouched('language', true)}
                        error={Boolean(touched.language && errors.language)}
                        label="Contact Language"
                        options={languages}
                        searchable={false}
                        InputLabelProps={{
                          className: classes.fieldLabel,
                          shrink: true,
                        }}
                        InputProps={{ 'data-lh-id': 'create-patient-dialog-language' }}
                      />
                    </Grid>
                    {isChineseLanguage && (
                      <Grid item sm={6}>
                        <SelectField
                          value={values.culture}
                          // @ts-ignore
                          onChange={(value: string) => setFieldValue('culture', value)}
                          onBlur={() => setFieldTouched('language', true)}
                          error={Boolean(touched.culture && errors.culture)}
                          label="Culture"
                          options={CHINESE_CULTURE_LANGUAGE_OPTIONS}
                          searchable={false}
                          InputLabelProps={{
                            className: classes.fieldLabel,
                            shrink: true,
                          }}
                          InputProps={{ 'data-lh-id': 'create-patient-dialog-culture' }}
                        />
                      </Grid>
                    )}
                    <Grid item sm={12}>
                      <UserContactFieldSelect
                        label="Contact"
                        value={values.contact}
                        excludedOptions={excludedOptions}
                        onChangeContactType={(type: any) => setFieldValue('contact.type', type)}
                        onChangeContactValue={(e: any) => setFieldValue('contact.value', e.target.value)}
                        onBlur={() => setFieldTouched('contact', true)}
                        error={Boolean(touched.contact && errors.contact)}
                        helperText={touched.contact && errors.contact && errors.contact.value}
                        InputLabelProps={{
                          className: classNames(classes.fieldLabel, classes.contactFieldLabel),
                          shrink: true,
                        }}
                      />
                    </Grid>
                    {linkToEhrOption && (
                      <Grid item sm={6}>
                        <div className={classes.checkboxWrapper}>
                          <Checkbox
                            icon={<CheckBoxOutlineBlankIcon fontSize="default" />}
                            checkedIcon={<CheckBoxIcon fontSize="default" />}
                            checked={values.ehrEnable}
                            color="primary"
                            disableRipple
                            className={classes.checkbox}
                            onChange={(e: any, checked: any) => setFieldValue('ehrEnable', checked)}
                            inputProps={{
                              'data-lh-id': 'create-patient-dialog-ehrEnable_checkbox',
                            }}
                          />
                          <Typography className={classes.checkboxLabel} color="primary">
                            Link patient to EHR
                          </Typography>
                        </div>
                      </Grid>
                    )}
                    <Grid item sm={12}>
                      {values.ehrEnable && (
                        <TextField
                          placeholder="EHR ID"
                          value={values.ehrId}
                          onChange={(e) => setFieldValue('ehrId', e.target.value)}
                          onBlur={() => setFieldTouched('ehrId', true)}
                          error={Boolean(touched.ehrId && errors.ehrId)}
                          InputProps={{
                            classes: {
                              input: classes.input,
                            },
                            'data-lh-id': 'create-patient-dialog-ehrId',
                          }}
                          label="EHR ID"
                          InputLabelProps={{
                            className: classes.fieldLabel,
                            shrink: true,
                          }}
                          fullWidth
                        />
                      )}
                    </Grid>
                  </Grid>
                </Form>
              </DialogContent>
              <DialogActions className={classes.footer} disableActionSpacing>
                <Button
                  disabled={isSubmitting}
                  type="button"
                  variant="text"
                  size="large"
                  color="primary"
                  onClick={() => onClose()}
                  data-lh-id="create-patient-dialog-cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  classes={{ root: classes.button, label: classes.buttonLabel }}
                  form={FORM_NAME}
                  disabled={isSubmitting}
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  data-lh-id="create-patient-dialog-create-button"
                >
                  Create
                </Button>
              </DialogActions>
            </React.Fragment>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default withStyles(styles)(DialogPatientCreate);
